import React, { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';


// TypeScript types for type safety and better maintainability
type RouteConfig = {
    path: string;
    element: React.ReactElement;
    showInNav?: boolean;
    public?: boolean;
    right?: boolean;
};

type Routes = {
    [key: string]: RouteConfig;
};

// Lazy load components for performance optimization - reduces initial bundle size
const LazyLogin = lazy(() => import("components/pages/Login"));
const LazyHome = lazy(() => import("components/pages/Home"));
const LazyLogout = lazy(() => import("components/pages/Logout"));
const LazyGiftCards = lazy(() => import("components/pages/GiftCards"));
const LazySongs = lazy(() => import("components/pages/Songs"));
const LazySongView = lazy(() => import("components/pages/SongView"));
const LazyChords = lazy(() => import("components/pages/Chords"));
const LazyNotes = lazy(() => import("components/pages/Notes"));
const LazyProfile = lazy(() => import("components/pages/Profile"));
const LazyGames = lazy(() => import("components/pages/Games"));

// Wrapper component for lazy loading with Suspense fallback for better UX
const LazyWrapper: React.FC<{ children: React.ReactElement }> = ({ children }) => (
    <Suspense fallback={<div>Loading...</div>}>
        {children}
    </Suspense>
);

// Routes configuration with improved structure, grouping, and organization
const routes: Routes = {
    Login: {
        path: '/',
        element: <LazyWrapper><LazyLogin /></LazyWrapper>,
        showInNav: false,
    },

    // Main application routes
    Home: {
        path: '/home',
        element: <LazyWrapper><LazyHome /></LazyWrapper>,
    },
    GiftCards: {
        path: '/giftcards',
        element: <LazyWrapper><LazyGiftCards /></LazyWrapper>,
    },

    // Guitar-related routes grouped together
    Songs: {
        path: '/guitar/songs',
        element: <LazyWrapper><LazySongs /></LazyWrapper>,
        public: true,
    },
    SongView: {
        path: '/guitar/songs/:songId',
        element: <LazyWrapper><LazySongView /></LazyWrapper>,
        showInNav: false,
    },
    Chords: {
        path: '/guitar/chords',
        element: <LazyWrapper><LazyChords /></LazyWrapper>,
        public: true,
    },

    Games: {
        path: '/games',
        element: <LazyWrapper><LazyGames /></LazyWrapper>,
        public: true,
    },

    Notes: {
        path: '/notes',
        element: <LazyWrapper><LazyNotes /></LazyWrapper>,
        showInNav: false,
    },

    Profile: {
        path: '/user/profile',
        element: <LazyWrapper><LazyProfile /></LazyWrapper>,
        showInNav: true,
        right: true,
    },
    Logout: {
        path: '/user/logout',
        element: <LazyWrapper><LazyLogout /></LazyWrapper>,
        right: true,
    },
};

// Validation function for route parameters - handles edge cases like invalid songId
export const validateRouteParam = (param: string, type: 'number' | 'string'): boolean => {
    if (type === 'number') {
        return !isNaN(Number(param)) && Number(param) > 0; // Ensure positive number for IDs
    }
    return typeof param === 'string' && param.trim().length > 0;
};

export default routes;